/**
 * Plans and Features Data
 * Defines all available plans and their features
 */

import { PlanFeature, PlanTierInfo } from '@/types/planTypes';

export const planTiers: PlanTierInfo[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    billingUnit: 'per user/month, billed annually',
    billingCycle: 'annually',
    description: 'Perfect for small teams getting started',
    buttonText: 'Downgrade',
    buttonAction: 'downgrade'
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 99,
    billingUnit: 'per user/month, billed annually',
    billingCycle: 'annually',
    description: 'Advanced AI and collaboration for growing teams',
    popular: true,
    currentPlan: true,
    buttonText: 'Current plan',
    buttonAction: 'current'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'custom',
    billingUnit: 'custom pricing',
    billingCycle: 'annually',
    description: 'Unlimited everything with premium support',
    buttonText: 'Talk to sales',
    buttonAction: 'contact'
  }
];

export const planFeatures: PlanFeature[] = [
  // Workspace
  {
    category: 'Workspace',
    name: 'User seats',
    description: 'Number of team members who can access the workspace',
    values: {
      starter: '5',
      professional: '25',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Workspace',
    name: 'Recording sessions',
    description: 'Number of recording sessions per month',
    values: {
      starter: '100',
      professional: '1,000',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Workspace',
    name: 'Storage per workspace',
    values: {
      starter: '25 GB',
      professional: '250 GB',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Workspace',
    name: 'Custom templates',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Workspace',
    name: 'Template categories',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Workspace',
    name: 'Custom branding',
    description: 'Company logo and custom colors',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },

  // Recording & Transcription
  {
    category: 'Recording & Transcription',
    name: 'Live transcription',
    description: 'Real-time transcription during recording',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Post-session transcription',
    description: 'Process uploaded audio files',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Audio file uploads',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Speaker identification',
    description: 'Number of speakers that can be identified',
    values: {
      starter: 'Up to 3',
      professional: 'Up to 10',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Transcription quality',
    values: {
      starter: 'Standard',
      professional: 'High',
      enterprise: 'Premium'
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Recording duration limit',
    description: 'Maximum length per recording session',
    values: {
      starter: '1 hour',
      professional: '4 hours',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Recording & Transcription',
    name: 'Batch processing',
    description: 'Upload multiple files at once',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },

  // AI Features
  {
    category: 'AI Features',
    name: 'AI-powered question filling',
    description: 'Automatically fill template questions from transcript',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'AI Features',
    name: 'AI chat assistant',
    description: 'Ask questions about your transcripts',
    values: {
      starter: false,
      professional: 'Basic',
      enterprise: 'Advanced'
    }
  },
  {
    category: 'AI Features',
    name: 'AI processing credits',
    description: 'Credits for AI-powered features per month',
    values: {
      starter: '500',
      professional: '5,000',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'AI Features',
    name: 'Additional credits',
    values: {
      starter: '$0.01 per credit',
      professional: '$0.01 per credit',
      enterprise: 'Included'
    }
  },
  {
    category: 'AI Features',
    name: 'Custom AI workflows',
    description: 'Create automated processing workflows',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'AI Features',
    name: 'Speaker assignment',
    description: 'AI-powered speaker identification',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'AI Features',
    name: 'Transcript summaries',
    description: 'AI-generated session summaries',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },

  // Collaboration & Sharing
  {
    category: 'Collaboration & Sharing',
    name: 'Session sharing',
    description: 'Share sessions with team members',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Collaboration & Sharing',
    name: 'Real-time collaboration',
    description: 'Multiple users viewing/editing simultaneously',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Collaboration & Sharing',
    name: 'Distribution groups',
    description: 'Organize users into groups for easy sharing',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Collaboration & Sharing',
    name: 'Comments & notes',
    description: 'Add comments to transcripts',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Collaboration & Sharing',
    name: 'Task assignments',
    description: 'Assign follow-up tasks from sessions',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Collaboration & Sharing',
    name: 'Approval workflows',
    description: 'Review and approve sessions before distribution',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },

  // Documents & Export
  {
    category: 'Documents & Export',
    name: 'Document export',
    description: 'Export to PDF, Word, or JSON',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Documents & Export',
    name: 'Bulk export',
    description: 'Export multiple sessions at once',
    values: {
      starter: 'Up to 5',
      professional: 'Up to 50',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Documents & Export',
    name: 'Custom document templates',
    description: 'Design your own export formats',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Documents & Export',
    name: 'API access',
    description: 'Programmatic access to your data',
    values: {
      starter: false,
      professional: 'Standard',
      enterprise: 'Full access'
    }
  },
  {
    category: 'Documents & Export',
    name: 'Data retention',
    description: 'How long we keep your data',
    values: {
      starter: '1 year',
      professional: '3 years',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Documents & Export',
    name: 'Automated backups',
    values: {
      starter: 'Weekly',
      professional: 'Daily',
      enterprise: 'Real-time'
    }
  },

  // People Management
  {
    category: 'People Management',
    name: 'People records',
    description: 'Track individuals across sessions',
    values: {
      starter: 'Up to 250',
      professional: 'Up to 2,500',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'People Management',
    name: 'Custom person fields',
    description: 'Add custom attributes to people records',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'People Management',
    name: 'Advanced search',
    description: 'Search and filter people records',
    values: {
      starter: 'Basic',
      professional: 'Advanced',
      enterprise: 'Advanced'
    }
  },
  {
    category: 'People Management',
    name: 'Session history',
    description: 'View all sessions for a person',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'People Management',
    name: 'Person profiles',
    description: 'Detailed profiles with photo and info',
    values: {
      starter: false,
      professional: true,
      enterprise: true
    }
  },

  // Security & Compliance
  {
    category: 'Security & Compliance',
    name: 'User roles & permissions',
    description: 'Control what users can see and do',
    values: {
      starter: 'Basic',
      professional: 'Advanced',
      enterprise: 'Custom'
    }
  },
  {
    category: 'Security & Compliance',
    name: 'Single Sign-On (SSO)',
    description: 'SAML 2.0 integration',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },
  {
    category: 'Security & Compliance',
    name: 'Two-factor authentication',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Security & Compliance',
    name: 'Audit logs',
    description: 'Track all user actions and changes',
    values: {
      starter: false,
      professional: '90 days',
      enterprise: 'Unlimited'
    }
  },
  {
    category: 'Security & Compliance',
    name: 'HIPAA compliance',
    description: 'Healthcare data protection',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },
  {
    category: 'Security & Compliance',
    name: 'Data encryption',
    values: {
      starter: 'In transit',
      professional: 'In transit & at rest',
      enterprise: 'In transit & at rest'
    }
  },
  {
    category: 'Security & Compliance',
    name: 'IP allowlisting',
    description: 'Restrict access by IP address',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },

  // Support
  {
    category: 'Support',
    name: 'Knowledge base',
    description: 'Self-service help articles and guides',
    values: {
      starter: true,
      professional: true,
      enterprise: true
    }
  },
  {
    category: 'Support',
    name: 'Email support',
    values: {
      starter: 'Business hours',
      professional: 'Priority (24h)',
      enterprise: 'Priority (4h)'
    }
  },
  {
    category: 'Support',
    name: 'Live chat support',
    values: {
      starter: false,
      professional: 'Business hours',
      enterprise: '24/7'
    }
  },
  {
    category: 'Support',
    name: 'Phone support',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },
  {
    category: 'Support',
    name: 'Dedicated account manager',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },
  {
    category: 'Support',
    name: 'Onboarding & training',
    values: {
      starter: 'Self-guided',
      professional: 'Guided setup',
      enterprise: 'Dedicated training'
    }
  },
  {
    category: 'Support',
    name: 'Custom integrations',
    description: 'Help building custom integrations',
    values: {
      starter: false,
      professional: false,
      enterprise: true
    }
  },
  {
    category: 'Support',
    name: 'SLA guarantee',
    description: 'Uptime guarantee',
    values: {
      starter: '99%',
      professional: '99.5%',
      enterprise: '99.9%'
    }
  }
];

// Group features by category
export const getFeaturesByCategory = () => {
  const categories = new Map<string, PlanFeature[]>();
  
  planFeatures.forEach(feature => {
    if (!categories.has(feature.category)) {
      categories.set(feature.category, []);
    }
    categories.get(feature.category)!.push(feature);
  });
  
  return categories;
};

// Get all unique categories
export const getCategories = () => {
  return Array.from(new Set(planFeatures.map(f => f.category)));
};

